import find from "lodash/find";
import get from "lodash/get";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import set from "lodash/set";
import size from "lodash/size";

import moment from "moment";
import { DATE_FORMATS, defaultFirebaseProjectId } from "../constants";
import i18n from "../i18n";
import { clipCustomWorkoutName } from "../utils/copy-paste-helpers";
import { saveInsertUndo } from "../utils/insert-undo";
import { setSession } from "../utils/session-helper";
import { normalizeLoomURL, validLoomURL } from "../utils/string-helpers";
import { jsonGetRoute } from "./json-get-route";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

async function quickEditRemoveSetRequests(
  { editExercise, editExercise: { sets } },
  api,
  proxyAthlete,
  reqSource,
  flash,
  index,
  organizationCoach = undefined
) {
  try {
    // If there is only one set left and user removes it, then delete exercise.
    let queryRequest = `/${editExercise._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}
    /${editExercise._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;

    if (organizationCoach) {
      queryRequest = queryRequest + `&organizationCoach=${organizationCoach}`;
    }

    if (sets.length === 1) {
      if (editExercise.exercises?.length) {
        await Promise.all(
          editExercise.exercises.map(async (ex) => {
            queryRequest = `/${ex._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
            if (organizationCoach) {
              queryRequest =
                queryRequest + `&organizationCoach=${organizationCoach}`;
            }
            return api(`exercise` + queryRequest, {
              method: "DELETE",
              body: ex,
            });
          })
        );
      } else {
        await api(`exercise` + queryRequest, {
          method: "DELETE",
          body: editExercise,
        });
      }
      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullyRemoved")
      );
    }
    // Otherwise, simply remove the single set.
    else {
      if (editExercise.exercises?.length) {
        await Promise.all(
          editExercise.exercises.map(async (ex) => {
            queryRequest = `/${ex._id}/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
            if (organizationCoach) {
              queryRequest =
                queryRequest + `&organizationCoach=${organizationCoach}`;
            }
            ex.sets.splice(index, 1);
            return api(`exercise` + queryRequest, {
              method: "PUT",
              body: ex,
            });
          })
        );
      } else {
        sets.splice(index, 1);
        await api(`exercise` + queryRequest, {
          method: "PUT",
          body: editExercise,
        });
      }
      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullyRemoved")
      );
    }
  } catch (err) {
    console.log("error:", err);
  }
}

function validationsForNewSet(newSet, defaultRepMax, isAssistant, assert) {
  assert(
    newSet.sets > 0,
    400,
    i18n.t("coaching-portal:exerciseActions.setsMustBeGreaterThanZero")
  );

  if (newSet.rpe !== "AMRAP") {
    assert(
      newSet.reps > 0,
      400,
      i18n.t("coaching-portal:exerciseActions.repsMustBeGreaterThanZero")
    );
  }

  assert(
    newSet.sets <= 500,
    400,
    i18n.t("coaching-portal:exerciseActions.tooManySets")
  );

  assert(
    isAssistant || defaultRepMax > 0,
    400,
    i18n.t("coaching-portal:exerciseActions.invalidRepMax")
  );
}

async function copySecondaryMovement(body, user, api, proxyAthlete) {
  if (!body || !body.primary || !body.movement) return;
  const library = get(user, `exerciseLibrary.${body.primary}`, []);
  const exists = find(library, { movement: body.movement });
  if (exists) return;

  const { primary, ...exercise } = body;

  try {
    await api(`exercise-library-secondary/${user._id}`, {
      method: "POST",
      ...(proxyAthlete ? { query: { proxyAthlete } } : {}),
      body: { primary, exercise },
    });
  } catch (_) {
    // Best-effort: a concurrent request (e.g. quick-edit saving several
    // exercises at once) may have already added this exact new movement.
    // The exercise itself still saves regardless of this convenience sync.
  }
}

const Exercise = (app) => {
  app
    // Client-side, no-page-reload fetch of exercises for an arbitrary date
    // range — mirrors js/load/exercise.js's own "activeExercises" query
    // construction exactly, so results match what the SSR loader would have
    // returned for the same range. Powers Nutrition Progress's on-demand
    // history extension (useExtendedHistoryData) when the coach picks a
    // range older than what the initial page load fetched (nutrition.
    // controller.js caps activeExercises to a trailing 1-year window).
    .get(
      "/exercise/active-range",
      jsonGetRoute(
        ({ req }) => {
          const { start, end, proxyAthlete } = req.query || {};
          return [
            "exercise",
            {
              query: {
                limit: false,
                sort: { order: 1, date: 1 },
                fields: [
                  "_id",
                  "primary",
                  "secondary",
                  "secondaryRepMaximum",
                  "primaryMuscleGroup",
                  "secondaryMuscleGroup",
                  "sets",
                  "repMaximums",
                  "notes",
                  "date",
                  "utcDate",
                  "order",
                  "type",
                  "cycle",
                  "parameters",
                  "instructions",
                  "nextSuperset",
                  "complex",
                  "complexName",
                  "indexInComplex",
                  "complexInstance",
                  "subType",
                  "duration",
                  "totalRounds",
                  "structure",
                  "restBetweenRounds",
                  "resultMeasurement",
                  "ladderDirection",
                  "ladderIncrement",
                  "metconResult",
                  "customName",
                  "repsPerSide",
                  "tempo",
                ],
                utcDate: {
                  $gte: Number.parseInt(moment(start).format("YYYYMMDD"), 10),
                  $lte: Number.parseInt(moment(end).format("YYYYMMDD"), 10),
                },
                $or: [
                  { cycle: { $exists: false } },
                  { "cycle.isScheduled": true },
                ],
                proxyAthlete,
                withThreads: true,
              },
            },
          ];
        },
        (data) => data ?? []
      )
    )
    // Quick Update
    .post(
      "/exercise/quick-edit",
      async ({ req, res, flash, locals, api, load }) => {
        const {
          body: { exercises, organizationCoach = undefined },
          query = {},
        } = req;

        const { proxyAthlete, reqSource } = query;

        await load("athlete", { user: proxyAthlete });

        let organizationQuery = ``;

        if (organizationCoach) {
          organizationQuery = `&organizationCoach=${organizationCoach}`;
        }

        const { athlete: profileUser } = locals;
        if (exercises.length) {
          await Promise.all(
            exercises.map(async (exercise) => {
              if (exercise.movementLibrary) {
                await copySecondaryMovement(
                  exercise.movementLibrary,
                  profileUser,
                  api,
                  proxyAthlete
                );
              }
              if (!exercise.tempo) exercise.tempo = null;
              return api(
                `exercise/${exercise._id}?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}` +
                  organizationQuery,
                { method: "PUT", body: exercise }
              );
            })
          );

          flash(
            "success",
            i18n.t("coaching-portal:exerciseActions.successfullySaved")
          );
        }
        res.refreshWith({ edit: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/exercise/quick-edit-note",
      async ({ req, res, flash, api, load }) => {
        const { body } = req;

        await api(`workout-notes`, { method: "POST", body });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySaved")
        );

        res.backDontScroll();
      }
    )
    .post(
      "/exercise/quick-edit-coach-note",
      async ({ req, res, flash, api, load }) => {
        const { body } = req;

        await api(`coach-notes`, { method: "POST", body });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySaved")
        );

        res.backDontScroll();
      }
    )
    // Create or update an exercise
    .post(
      "/exercise/save/:exercise?",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { body, params, query = {} } = req;
        const { proxyAthlete, reqSource } = query;

        const organizationCoach = body.organizationCoach || undefined;

        // proxyAthlete and profileUser if action is by coach.
        await Promise.all([
          load("editExercise", {
            exercise: params.exercise,
            proxyAthlete,
            organizationCoach: organizationCoach,
          }),
          load("athlete", { user: proxyAthlete }),
        ]);

        const { editExercise, athlete: profileUser } = locals;

        const activeUser = profileUser || locals.activeUserToRequest;
        const _id = get(editExercise, "_id");

        const isNew = !_id;

        const isAssistant =
          get(editExercise, "type", body.type) === "assistant";

        if (isAssistant) {
          body.sets = body.sets.map((set) => {
            // clean up if equipped when it was initally set then type changed
            if (set.equipment) delete set.equipment;
            if (set.equipped) delete set.equipped;
            if (set.equippedRepMax) delete set.equippedRepMax;

            if (set.load || set.rpe) return set;
            set.load = 0;
            return set;
          });
        }

        body.user = activeUser._id;

        if (!body.cycle)
          assert(
            body.date,
            400,
            i18n.t("coaching-portal:exerciseActions.specifyDayForWorkout")
          );
        assert(
          body.primary,
          400,
          i18n.t("coaching-portal:exerciseActions.primaryExerciseRequired")
        );
        if (includes(["secondary", "assistant"], body.type))
          assert(
            body.secondary,
            400,
            i18n.t("coaching-portal:exerciseActions.specifyTypeExercise", {
              type: body.type,
            })
          );

        assert(
          size(body.sets),
          400,
          i18n.t("coaching-portal:exerciseActions.addAtLeastOneSet")
        );

        _(body)
          .set("sets", body.sets || null)
          .set("notes", body.notes || null)
          .set("secondary", body.secondary || null)
          .set("parameters", body.parameters || null)
          .set("video", body.video || null)
          .set("instructions", body.instructions || null)
          .set("exerciseTypeField", body.exerciseTypeField || null)
          .set("primaryMuscleGroup", body.primaryMuscleGroup || null)
          .set("secondaryMuscleGroup", body.secondaryMuscleGroup || null)
          .set("customName", body.customName || null)
          .set("structure", body.structure || null)
          .set("tempo", body.tempo || null)
          .defaultsDeep(
            { repMaximums: editExercise.repMaximums },
            { repMaximums: activeUser.repMaximums }
          )
          .value();

        assert(
          isAssistant || !isEmpty(body.repMaximums),
          400,
          i18n.t("coaching-portal:exerciseActions.specifyRepMaxForExercise")
        );
        await copySecondaryMovement(
          body.movementLibrary,
          profileUser,
          api,
          proxyAthlete
        );
        delete body.movementLibrary;
        delete body.organizationCoach;

        body.sets.forEach((set) => {
          if (typeof set.reps === "string" && set.reps.includes("-")) {
            const [repsLower, reps] = set.reps.split("-");
            set.reps = reps;
            set.repsLower = repsLower;
          }
        });

        let exerciseQuery = `/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;

        if (organizationCoach) {
          exerciseQuery = `${exerciseQuery}&organizationCoach=${organizationCoach}`;
        }

        if (isNew) {
          await api(`exercise${exerciseQuery}`, { method: "POST", body });
          flash(
            "success",
            i18n.t("coaching-portal:exerciseActions.successfullyAdded")
          );
        } else {
          await api(`exercise/${_id}${exerciseQuery}`, { method: "PUT", body });
          flash(
            "success",
            i18n.t("coaching-portal:exerciseActions.successfullyUpdated")
          );
        }

        res.refreshWith(
          {
            modal: "",
            editMode: "",
            movement: "",
            scrollTo: _id,
            secondaryAdded: "",
            addType: "",
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Save the add-exercise modal's workout queue (several exercises plus
    // optional section separators) with a single API request.
    .post(
      "/exercise/save-workout-queue",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource } = query;
        const {
          target,
          workoutName,
          workoutNote,
          items = [],
          queueStorageKey,
          insert,
        } = body;
        const organizationCoach = body.organizationCoach || undefined;

        await load("athlete", { user: proxyAthlete });
        const { athlete: profileUser } = locals;
        const activeUser = profileUser || locals.activeUserToRequest;

        assert(
          target?.cycle || target?.date,
          400,
          i18n.t("coaching-portal:exerciseActions.specifyDayForWorkout")
        );
        const exercises = items.filter((item) => item.kind === "exercise");
        // A note-only save (no exercises queued) is legal — the queue's
        // "Instructions (Optional)" row works even with an empty queue.
        const hasNoteToSave =
          typeof workoutNote === "string" && !!workoutNote.trim();
        assert(
          exercises.length > 0 || hasNoteToSave,
          400,
          i18n.t("coaching-portal:exerciseActions.addAtLeastOneSet")
        );

        // Same per-exercise guarantees as /exercise/save above.
        for (const exercise of exercises) {
          const isAssistant = exercise.type === "assistant";

          if (isAssistant && Array.isArray(exercise.sets)) {
            exercise.sets = exercise.sets.map((set) => {
              // clean up if equipped when it was initally set then type changed
              if (set.equipment) delete set.equipment;
              if (set.equipped) delete set.equipped;
              if (set.equippedRepMax) delete set.equippedRepMax;

              if (set.load || set.rpe) return set;
              set.load = 0;
              return set;
            });
          }

          assert(
            exercise.primary,
            400,
            i18n.t("coaching-portal:exerciseActions.primaryExerciseRequired")
          );
          if (includes(["secondary", "assistant"], exercise.type))
            assert(
              exercise.secondary,
              400,
              i18n.t("coaching-portal:exerciseActions.specifyTypeExercise", {
                type: exercise.type,
              })
            );
          assert(
            size(exercise.sets),
            400,
            i18n.t("coaching-portal:exerciseActions.addAtLeastOneSet")
          );

          exercise.user = activeUser._id;
          exercise.notes = exercise.notes || null;
          exercise.secondary = exercise.secondary || null;
          exercise.parameters = exercise.parameters || null;
          exercise.video = exercise.video || null;
          exercise.instructions = exercise.instructions || null;
          exercise.exerciseTypeField = exercise.exerciseTypeField || null;
          exercise.primaryMuscleGroup = exercise.primaryMuscleGroup || null;
          exercise.secondaryMuscleGroup = exercise.secondaryMuscleGroup || null;
          exercise.tempo = exercise.tempo || null;
          if (isEmpty(exercise.repMaximums))
            exercise.repMaximums = activeUser.repMaximums;

          assert(
            isAssistant || !isEmpty(exercise.repMaximums),
            400,
            i18n.t("coaching-portal:exerciseActions.specifyRepMaxForExercise")
          );

          // Secondary/assistant movement library adds are applied
          // atomically, server-side, as part of the save-workout-queue
          // request itself (exercise.movementLibrary is forwarded as-is)
          // instead of one PUT per exercise here.

          exercise.sets.forEach((set) => {
            if (typeof set.reps === "string" && set.reps.includes("-")) {
              const [repsLower, reps] = set.reps.split("-");
              set.reps = reps;
              set.repsLower = repsLower;
            }
          });
        }

        let queueQuery = `?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
        if (organizationCoach)
          queueQuery += `&organizationCoach=${organizationCoach}`;

        const saveRes = await api(`save-workout-queue${queueQuery}`, {
          method: "POST",
          body: {
            target,
            workoutName: clipCustomWorkoutName(workoutName),
            // Server-normalized (trim + clip + undefined-means-untouched) by
            // normalizeWorkoutNote — forwarded as-is.
            workoutNote,
            items,
            // Insert above/below an exercise instead of appending; the API
            // computes orders and circuit links (program targets only).
            ...(insert?.targetExerciseId ? { insert } : {}),
          },
        });

        // Actions run in the browser: drop the persisted queue only after the
        // API accepted it, so a failed save keeps the queue restorable.
        if (process.browser && queueStorageKey)
          localStorage.removeItem(queueStorageKey);

        // Insert: leave the snapshot for the editor's Undo toast, which
        // replaces the "saved" flash.
        let hasUndoToast = false;
        if (process.browser && insert?.targetExerciseId) {
          const saved = await saveRes.json();
          if (saved?.snapshot) {
            saveInsertUndo({
              snapshot: saved.snapshot,
              count: exercises.length,
            });
            hasUndoToast = true;
          }
        }

        if (!hasUndoToast)
          flash(
            "success",
            exercises.length > 0
              ? i18n.t("coaching-portal:exerciseActions.workoutQueueSaved", {
                  count: exercises.length,
                })
              : i18n.t("coaching-portal:exerciseActions.workoutNoteSaved")
          );

        res.refreshWith(
          {
            insertTarget: "",
            insertPosition: "",
            modal: "",
            editMode: "",
            movement: "",
            secondaryAdded: "",
            addType: "",
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Remove an exercise
    .get(
      "/exercise/remove/:exercise",
      async ({ req, res, assert, flash, locals, api, load, session }) => {
        if (
          process.browser &&
          !confirm(
            i18n.t("coaching-portal:exerciseActions.confirmDeleteExercise")
          )
        ) {
          return res.backDontScroll();
        }

        const {
          params,
          query: { proxyAthlete },
        } = req;

        let editExerciseQuery = { exercise: params.exercise, proxyAthlete };
        if (locals.activeUser.organizationId) {
          editExerciseQuery = {
            ...editExerciseQuery,
            organizationCoach: locals.activeUserToRequest._id,
          };
        }
        await load("editExercise", editExerciseQuery);
        const { editExercise, activeUserToRequest } = locals;

        assert(
          editExercise,
          400,
          i18n.t("coaching-portal:exerciseActions.exerciseAlreadyRemoved")
        );

        await api(
          `exercise/${editExercise._id}?proxyAthlete=${proxyAthlete}&organizationCoach=${activeUserToRequest._id}`,
          {
            method: "DELETE",
          }
        );

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyRemoved")
        );
        res.backDontScroll();
      }
    )
    // Unschedule a workout: Remove an entire workout (list of exercises).
    .post(
      "/exercise/unschedule-workout",
      async ({ req, res, assert, flash, api, locals, load }) => {
        const { body, query = {} } = req;
        const { silent, source = "" } = body;
        const { proxyAthlete } = query;

        if (proxyAthlete) {
          locals.athlete = get(locals.activeUser, "athletes", []).find(
            (a) => String(a._id) === proxyAthlete
          );
        }
        delete body.silent;
        delete body.source;
        assert(
          body.days,
          400,
          i18n.t("coaching-portal:exerciseActions.selectWorkoutToRemove")
        );
        // trying the remove all logic
        const startingCalendarDay = locals.activeUser.startingCalendarDay || 0;
        const getWeekStart = (m, dow) => {
          const offset = (m.day() - dow + 7) % 7;
          return m.clone().subtract(offset, "days").startOf("day");
        };
        const getWeekEnd = (m, dow) => {
          return getWeekStart(m, dow).add(6, "days").endOf("day");
        };
        let period = moment(body.days[0], DATE_DAY_FORMAT),
          start,
          end;
        switch (source) {
          case "calendarDayView":
            start = period.clone().add(-1, "days");
            end = period.clone().add(1, "days");
            break;

          case "calendarWeekView":
            start = getWeekStart(period, startingCalendarDay);
            end = getWeekEnd(period, startingCalendarDay);
            break;

          default: {
            const sortedDays = [...body.days].sort();
            const monthStart = moment(sortedDays[0], DATE_DAY_FORMAT);
            const monthEnd = moment(sortedDays.at(-1), DATE_DAY_FORMAT);
            start = getWeekStart(
              monthStart.clone().startOf("month"),
              startingCalendarDay
            );
            end = getWeekEnd(
              monthEnd.clone().endOf("month"),
              startingCalendarDay
            );
          }
        }
        await load("activeExercises", { start, end, proxyAthlete });
        const exercises = locals.activeExercises
          .filter((ex) => {
            return body.days.includes(
              moment(ex.utcDate.toString()).format(DATE_DAY_FORMAT)
            );
          })
          .map((exercise) => exercise._id);
        await api(`exercise-remove-multiple?proxyAthlete=${proxyAthlete}`, {
          method: "POST",
          body: {
            exercises,
            days: body.days,
          },
        });
        flash(
          "success",
          silent
            ? i18n.t(
                "coaching-portal:exerciseActions.workoutUnscheduledClickAdd"
              )
            : i18n.t(
                "coaching-portal:exerciseActions.workoutSuccessfullyUnscheduled"
              )
        );

        res.backDontScroll();
        // END of remove all logic
      }
    )
    // Copy one day of exercises to another.
    .post(
      "/exercise/copy-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource } = query;
        const { type } = body;
        body.to = moment(body.to).format("YYYY-MM-DD") + "T00:00:00.000Z";
        assert(
          !moment.utc(body.from).isSame(moment.utc(body.to), "day"),
          400,
          i18n.t("coaching-portal:exerciseActions.cannotTypeWorkoutToSameDay", {
            type,
          })
        );
        await api(
          `copy-workout?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: omit(body, "type") }
        );
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyType", { type })
        );

        res.refreshWith(
          { modal: "", day: moment.utc(body.to).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/paste-exercises",
      async ({ req, res, flash, api, locals }) => {
        const { body } = req;
        const {
          activeUserToRequest: { primaryExercises = {}, _id: ownerId } = {},
        } = locals;
        const exerciseInLocalStorage = localStorage.getItem("exercisesToCopy");
        if (exerciseInLocalStorage == null || exerciseInLocalStorage === "") {
          flash("error", "Nothing to paste");
          return res.backDontScroll();
        }
        let exercises;
        try {
          exercises = JSON.parse(exerciseInLocalStorage);
        } catch (parseErr) {
          flash("error", "Invalid exercise clipboard");
          return res.backDontScroll();
        }
        if (!Array.isArray(exercises)) {
          flash("error", "Invalid exercise clipboard");
          return res.backDontScroll();
        }

        const endpoint = body.isProgram
          ? `paste-exercises-program`
          : `paste-exercises-calendar`;

        // Section separators copied alongside the workout (best-effort).
        let sections = [];
        const sectionsInLocalStorage = localStorage.getItem("sectionsToCopy");
        if (sectionsInLocalStorage) {
          try {
            const parsed = JSON.parse(sectionsInLocalStorage);
            if (Array.isArray(parsed)) sections = parsed;
          } catch {
            sections = [];
          }
        }

        const { isProgram, ...restBody } = body; //deleting isProgram from the body
        const updatedBody = {
          ...restBody,
          userInSessionPrimaryExercises: primaryExercises,
          exercises,
          sections,
          ownerId,
        };
        if (body.isProgram) {
          const nameFromClipboard = localStorage.getItem("workoutNameToCopy");
          const clippedName = clipCustomWorkoutName(nameFromClipboard);
          if (clippedName) {
            updatedBody.pastedWorkoutName = clippedName;
          }
        }
        await api(endpoint, {
          method: "POST",
          body: updatedBody,
        });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyPasted")
        );

        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )

    .post(
      "/exercise/import-workout",
      async ({ req, res, flash, api, locals }) => {
        const { body } = req;
        const {
          activeUserToRequest: { primaryExercises = {}, _id: ownerId } = {},
        } = locals;
        const {
          workoutCycleId, // source single-workout template cycle id
          workoutCycleIds, // insert mode: several templates, imported in this order
          workoutNames, // insert mode: names of workoutCycleIds, same order (error messages)
          insert, // optional { targetExerciseId, position } (program editor only)
          cycleId, // target program cycle id (program editor only)
          programNumber,
          sessionProgram, // { week, order } of the target session (program editor)
          date, // target calendar day (calendar only)
          exercisesLength = 0,
          highestOrder = 0,
          athlete, // { _id, repMaximums } of the target user (coach for templates)
        } = body;

        const sourceIds = (
          Array.isArray(workoutCycleIds) && workoutCycleIds.length
            ? workoutCycleIds
            : [workoutCycleId]
        ).filter(Boolean);
        const isInsert = Boolean((cycleId || date) && insert?.targetExerciseId);

        if (sourceIds.length === 0 || (sourceIds.length > 1 && !isInsert)) {
          flash("error", i18n.t("athlete-portal:programs.importWorkoutError"));
          return res.backDontScroll();
        }

        // The single-workout template's exercises belong to the coach; fetch them by
        // cycle id (same way the program editor loads exercises) and reuse the paste
        // pipeline so loads/reps and library validation behave like "Paste Workout".
        const orgQuery = locals.activeUser.organizationId
          ? { organizationCoach: ownerId }
          : {};

        const exercises = [];
        const sections = [];
        // Several workouts are flattened into one list; each one's orders are
        // shifted past the previous one so the combined order stays stable.
        let orderOffset = 0;
        for (const [sourceIndex, sourceId] of sourceIds.entries()) {
          const exercisesRes = await api("exercise", {
            query: {
              limit: false,
              "cycle.id": sourceId,
              sort: { order: 1 },
              ...orgQuery,
            },
          });
          const workoutExercises = await exercisesRes.json();

          if (
            !Array.isArray(workoutExercises) ||
            workoutExercises.length === 0
          ) {
            const emptyName = Array.isArray(workoutNames)
              ? workoutNames[sourceIndex]
              : undefined;
            flash(
              "error",
              emptyName
                ? i18n.t("athlete-portal:programs.importWorkoutEmptyNamed", {
                    name: emptyName,
                  })
                : i18n.t("athlete-portal:programs.importWorkoutEmpty")
            );
            return res.backDontScroll();
          }

          // The template's section separators live in the same cycle; carry them
          // through the paste pipeline so the imported workout keeps its sections.
          let workoutSections = [];
          try {
            const sectionsRes = await api("exercise-section", {
              query: {
                limit: false,
                "cycle.id": sourceId,
                sort: { order: 1 },
                ...orgQuery,
              },
            });
            const sectionsJson = await sectionsRes.json();
            if (Array.isArray(sectionsJson)) {
              // Preserve a workout note's type/description (instead of
              // collapsing it to `name`) so it survives the import.
              workoutSections = sectionsJson.map((s) =>
                s.type === "workout-note"
                  ? {
                      type: "workout-note",
                      description: s.description ?? "",
                      order: s.order ?? 0,
                    }
                  : { type: "normal", name: s.name ?? "", order: s.order ?? 0 }
              );
            }
          } catch {
            workoutSections = [];
          }

          exercises.push(
            ...workoutExercises.map((exercise) => ({
              ...exercise,
              order: (exercise.order ?? 0) + orderOffset,
            }))
          );
          sections.push(
            ...workoutSections.map((section) =>
              section.type === "workout-note"
                ? section
                : { ...section, order: section.order + orderOffset }
            )
          );
          orderOffset =
            Math.max(...workoutExercises.map((e) => e.order ?? 0)) +
            1 +
            orderOffset;
        }

        // A program target carries a cycleId/session; otherwise we're importing onto a
        // calendar day, which routes through the calendar paste endpoint instead.
        const isProgram = Boolean(cycleId);
        const sharedBody = {
          exercises,
          sections,
          athlete,
          ownerId,
          userInSessionPrimaryExercises: primaryExercises,
          exercisesLength,
          highestOrder,
        };

        let hasUndoToast = false;
        if (isProgram) {
          const pasteRes = await api("paste-exercises-program", {
            method: "POST",
            body: {
              ...sharedBody,
              date: moment().format(DATE_DAY_FORMAT),
              sessionProgram,
              programNumber,
              cycleId,
              ...(isInsert ? { insert } : {}),
            },
          });
          // Insert: leave the snapshot for the editor's Undo toast.
          if (process.browser && isInsert) {
            const pasted = await pasteRes.json();
            if (pasted?.snapshot) {
              saveInsertUndo({
                snapshot: pasted.snapshot,
                count: pasted.exercises?.length || 0,
              });
              hasUndoToast = true;
            }
          }
        } else {
          // Insert acts on the athlete's day, so the request runs as that
          // athlete (proxyAthlete) and the API checks the paste is theirs.
          const pasteRes = await api(
            `paste-exercises-calendar${
              isInsert ? `?proxyAthlete=${athlete._id}` : ""
            }`,
            {
              method: "POST",
              body: {
                ...sharedBody,
                date,
                ...(isInsert ? { insert } : {}),
              },
            }
          );
          if (process.browser && isInsert) {
            const pasted = await pasteRes.json();
            if (pasted?.snapshot) {
              saveInsertUndo({
                snapshot: pasted.snapshot,
                count: pasted.exercises?.length || 0,
              });
              hasUndoToast = true;
            }
          }
        }

        // The Undo toast replaces the flash for an insert.
        if (!hasUndoToast)
          flash(
            "success",
            i18n.t("athlete-portal:programs.importWorkoutSuccess")
          );
        res.refreshWith(
          { modal: "", insertTarget: "", insertPosition: "" },
          { url: "back" }
        );
      }
    )

    // Undo the last insert above/below an exercise (see InsertUndoToast).
    .post("/exercise/insert-undo", async ({ req, res, flash, api, locals }) => {
      const { snapshot } = req.body;
      const organizationCoach = locals.activeUser?.organizationId
        ? locals.activeUserToRequest?._id
        : undefined;
      // A calendar insert is undone as the athlete whose day it is.
      const undoQuery = snapshot?.day
        ? `?proxyAthlete=${snapshot.day.user}`
        : organizationCoach
          ? `?organizationCoach=${organizationCoach}`
          : "";
      await api(`exercise-insert-undo${undoQuery}`, {
        method: "POST",
        body: { snapshot },
      });
      flash("success", i18n.t("athlete-portal:programs.insert.undone"));
      res.refreshWith({ modal: "" }, { url: "back" });
    })

    .post("/exercise/copy-exercises", async ({ req, res, flash }) => {
      const { body } = req;
      const {
        exercises,
        originalAthleteLibrary = [],
        workoutNameToCopy,
        sections,
      } = body;
      const now = new Date();
      const timestamp = now.getTime();
      const queryToRefresh = `${exercises[0]?._id}${timestamp}`;
      localStorage.setItem("exercisesToCopy", JSON.stringify(exercises));
      localStorage.setItem(
        "originalAthleteLibraryToCopy",
        JSON.stringify(originalAthleteLibrary)
      );
      // Section separators copied with the workout (cleared when none).
      if (Array.isArray(sections) && sections.length > 0) {
        localStorage.setItem("sectionsToCopy", JSON.stringify(sections));
      } else {
        localStorage.removeItem("sectionsToCopy");
      }
      if (workoutNameToCopy !== undefined) {
        const clipped = clipCustomWorkoutName(workoutNameToCopy);
        if (clipped) {
          localStorage.setItem("workoutNameToCopy", clipped);
        } else {
          localStorage.removeItem("workoutNameToCopy");
        }
      } else {
        localStorage.removeItem("workoutNameToCopy");
      }
      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullyCopied")
      );
      res.refreshWith(
        { exercisesInClipboard: queryToRefresh },
        { url: "back" }
      );
    })
    // Moves one day of exercises to another.
    .post(
      "/exercise/move-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource, dnd } = query;
        const { type } = body;
        assert(
          body.to,
          400,
          i18n.t("coaching-portal:exerciseActions.pleaseSelectDate")
        );
        body.to = moment(body.to).format("YYYY-MM-DD") + "T00:00:00.000Z";
        assert(
          !moment.utc(body.from).isSame(moment(body.to), "day"),
          400,
          i18n.t("coaching-portal:exerciseActions.cannotTypeWorkoutToSameDay", {
            type,
          })
        );
        await api(
          `move-workout?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: omit(body, "type") }
        );
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyTypedWorkout", {
            type,
          })
        );

        if (dnd) return res.backDontScroll();

        res.refreshWith(
          { modal: "", day: moment.utc(body.to).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/shift-day",
      async ({ req, res, assert, flash, api, session }) => {
        const { body, query = {} } = req;
        const { from } = body;
        const { proxyAthlete } = query;

        assert(
          from,
          400,
          i18n.t("coaching-portal:exerciseActions.pleaseSelectDate")
        );

        await api(`shift-program?proxyAthlete=${proxyAthlete}`, {
          method: "POST",
          body,
        });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyShifted")
        );

        res.refreshWith(
          { modal: "", day: moment.utc(from).format(DATE_DAY_FORMAT) },
          { url: "back" }
        );
      }
    )
    .post(
      "/exercise/order",
      async ({ req, res, api, assert, session, flash, locals }) => {
        const body = req.body;
        const athlete = get(req, "query.proxyAthlete");

        await api(`cycle-exercise-order?proxyAthlete=${athlete}`, {
          method: "POST",
          body,
        });
        res.refreshWith({}, { url: "back" });

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySaved")
        );
      }
    )
    .post("/exercise/reorder", async ({ req, res, api, flash, session }) => {
      const response = await api(
        `cycle-exercise-reorder?proxyAthlete=${get(req, "query.proxyAthlete")}`,
        { method: "POST", body: req.body }
      );
      const data = await response.json();
      setSession(session, "editCycle", data.cycle);
      setSession(session, "editExercises", data.programExercises);
      res.refreshWith({}, { url: "back" });

      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullySaved")
      );
    })
    .post(
      "/exercise/single-workout-order",
      async ({ req, res, api, flash, session }) => {
        const { body, query = {} } = req;
        const { proxyAthlete, reqSource } = query;
        await api(
          `order-exercises?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
          { method: "POST", body: body }
        );
        res.refreshWith({}, { url: "back" });

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySaved")
        );
      }
    )
    .post("/exercise/workout-reorder", async ({ req, res, api, flash }) => {
      const { body, query = {} } = req;
      const { proxyAthlete, reqSource } = query;
      if (body.toDate) {
        body.toDate =
          moment(body.toDate).format("YYYY-MM-DD") + "T00:00:00.000Z";
      }

      // The API scopes writes to the context user; without proxyAthlete that
      // is the coach, and nothing of the athlete's would be reordered.
      await api(
        `reorder-exercises?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`,
        { method: "POST", body: body }
      );
      res.backDontScroll();

      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullySaved")
      );
    })
    // Update rep max for exercise.
    .post(
      "/exercise/rep-max/:exercise?",
      async ({ req, res, api, load, locals, assert }) => {
        const { params, body } = req;
        await load("editExercise", { exercise: params.exercise });
        const { editExercise, activeUser } = locals;
        assert(
          body.primary,
          400,
          i18n.t("coaching-portal:exerciseActions.specifyPrimaryExercise")
        );
        assert(
          body.repMax,
          400,
          i18n.t("coaching-portal:exerciseActions.specifyRepMax")
        );
        set(editExercise, `repMaximums.${body.primary}`, body.repMax);

        if (body.overwrite) {
          set(activeUser, `repMaximums.${body.primary}`, body.repMax);
          await api(`user/${activeUser._id}`, {
            method: "PUT",
            body: { repMaximums: get(activeUser, "repMaximums", null) },
          });
        }

        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    // Create or update a set at a specific index in an exercise.
    .post(
      "/exercise/sets/save/:index/:exercise?",
      async ({ req, res, assert, flash, locals, api, load }) => {
        const { params, body } = req;
        await load("editExercise", { exercise: params.exercise });
        const { editExercise, activeUser } = locals;
        const index = get(params, "index");
        const primary = get(editExercise, "primary");
        const secondary = get(editExercise, "secondary", "");
        const repMaxPath = `repMaximums.${primary}`;
        const repMax = body.equipped
          ? body.equippedRepMax
          : get(editExercise, repMaxPath, get(activeUser, repMaxPath, 0));

        const isAssistant = get(editExercise, "type") === "assistant";

        assert(
          isAssistant || repMax,
          400,
          i18n.t(
            "coaching-portal:exerciseActions.repMaxGreaterThanZeroToAddSet"
          )
        );

        if (body.equipped) {
          assert(
            body.equipment,
            400,
            i18n.t(
              "coaching-portal:exerciseActions.specifyEquipmentForEquippedSet"
            )
          );
          assert(
            body.equippedRepMax > 0,
            400,
            i18n.t("coaching-portal:exerciseActions.specifyEquippedRepMax")
          );
        } else {
          delete body.equipment;
          delete body.equippedRepMax;
        }

        if (body.loadType === "%")
          body.load = repMax * (Number(body.percentRepMax) / 100);
        delete body.loadType;
        body.percentRepMax;

        assert(
          body.sets > 0,
          400,
          i18n.t("coaching-portal:exerciseActions.setsMustBeGreaterThanZero")
        );

        if (body.rpe !== "AMRAP") {
          assert(
            body.reps > 0,
            400,
            i18n.t("coaching-portal:exerciseActions.repsMustBeGreaterThanZero")
          );
        }

        if (process.browser && !isAssistant) {
          if (
            body.load > repMax &&
            !confirm(
              i18n.t("coaching-portal:exerciseActions.confirmProgramAbove100")
            )
          ) {
            return res.backDontScroll();
          }
        }

        editExercise.sets.splice(index, 1, body);

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyUpdated")
        );
        const modalToDisplay = get(body, "addAnother", false)
          ? { type: "edit-set" }
          : "";

        res.refreshWith({ modal: modalToDisplay }, { url: "back" });
      }
    )
    // Move a set within an exercise.
    .get(
      "/exercise/sets/move/:index/:to/:exercise?",
      async ({ req, res, locals, api, load, assert }) => {
        const { params } = req;
        await load("editExercise", { exercise: params.exercise });
        const {
          editExercise: { sets },
        } = locals;
        const [temp] = sets.splice(params.index, 1);
        sets.splice(params.to, 0, temp);
        res.backDontScroll();
      }
    )
    // Remove a set from an exercise.
    .get(
      "/exercise/sets/remove/:index/:exercise?",
      async ({ req, res, locals, load }, next) => {
        if (
          process.browser &&
          !confirm(i18n.t("coaching-portal:exerciseActions.confirmDeleteSet"))
        ) {
          return res.backDontScroll();
        }

        const { query, params } = req;
        await load("editExercise", { exercise: params.exercise });
        const {
          editExercise: { sets },
        } = locals;
        sets.splice(params.index, 1);
        res.backDontScroll();
      }
    )
    // Remove a set from an exercise in the quick-edit.
    .get(
      "/exercise/quick-edit/sets/remove/:index/:exercise/:organizationCoach/:proxyAthlete?",
      async ({ req, res, flash, locals, load, api }, next) => {
        const { params, query = {} } = req;
        const {
          exercise,
          index,
          organizationCoach = undefined,
          proxyAthlete,
        } = params;
        const { reqSource } = query;

        await load("editExercise", {
          exercise: exercise,
          proxyAthlete: proxyAthlete,
          organizationCoach: organizationCoach,
        });

        await quickEditRemoveSetRequests(
          locals,
          api,
          proxyAthlete,
          reqSource,
          flash,
          index,
          organizationCoach
        );

        res.refreshWith(
          {
            edit: true,
            editExerciseId: locals.editExercise._id,
            weekIndex: locals.editExercise.cycle.programWeek,
            sessionIndex: locals.editExercise.cycle.programSession - 1,
          },
          { url: "back" }
        );
      }
    )
    // Cancel the quick-edit.
    .get("/exercise/quick-edit/cancel", async ({ res }) => {
      res.refreshWith({ edit: "" }, { url: "back" });
    })
    // add a new set in the quick-edit.
    .post(
      "/exercise/quick-edit/sets/add/:index/:exercise/:proxyAthlete",
      async ({ req, res, assert, flash, locals, load, api }) => {
        const { body, params, query = {} } = req;
        const {
          newSet,
          defaultRepMax,
          organizationCoach = undefined,
          complexExercises,
          repsTwo,
          repsThree,
        } = body;
        const { exercise, index, proxyAthlete } = params;
        const { reqSource } = query;

        let exercisesToUpdate = [exercise];
        if (complexExercises?.length > 0) {
          exercisesToUpdate = [...exercisesToUpdate, ...complexExercises];
        }
        let exerciseEdited;
        for (const exId of exercisesToUpdate) {
          await load("editExercise", {
            exercise: exId,
            proxyAthlete: proxyAthlete,
            organizationCoach: organizationCoach,
          });
          const {
            editExercise,
            editExercise: { sets },
          } = locals;
          if (!editExercise.indexInComplex) exerciseEdited = editExercise;
          const isAssistant =
            get(editExercise, "type", body.type) === "assistant";

          const setToAdd = { ...newSet };
          if (editExercise.indexInComplex === 1)
            setToAdd.reps = repsTwo || newSet.reps;
          if (editExercise.indexInComplex === 2)
            setToAdd.reps = repsThree || newSet.reps;
          // Validations
          if (!editExercise.parameters) {
            validationsForNewSet(setToAdd, defaultRepMax, isAssistant, assert);
          }

          let queryRequest = `/?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;

          if (organizationCoach) {
            queryRequest =
              queryRequest + `&organizationCoach=${body.organizationCoach}`;
          }

          delete body.organizationCoach;

          try {
            // Adding the new set in index+1 of the array
            sets.splice(parseInt(index) + 1, 0, setToAdd);
            await api(`exercise/${editExercise._id}` + queryRequest, {
              method: "PUT",
              body: editExercise,
            });
          } catch (err) {
            console.log("error:", err);
          }
        }
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyAdded")
        );
        res.refreshWith(
          {
            edit: true,
            editExerciseId: exerciseEdited._id,
            weekIndex: exerciseEdited.cycle && exerciseEdited.cycle.programWeek,
            sessionIndex:
              exerciseEdited.cycle && exerciseEdited.cycle.programSession - 1,
            setIndex: index,
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Duplicates exercise in the Calendar single day view
    .post(
      "/exercise/duplicate-from-single-day",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query = {} } = req;
        const { proxyAthlete } = query;
        const _id = get(body, "_id");

        if (body.sets) {
          body.sets.forEach((set) => {
            if (
              set.video ||
              set.videoArray ||
              set.statusArray ||
              set.outcomes ||
              set.comments ||
              set.videos
            ) {
              delete set.video;
              delete set.videoArray;
              delete set.statusArray;
              delete set.outcomes;
              delete set.comments;
              delete set.videos;
            }
          });
        }

        delete body.nextSuperset;
        const cycle = await api(
          `exercise?proxyAthlete=${proxyAthlete}&reqSource=${"calendar"}`,
          {
            method: "POST",
            body,
          }
        );
        res.backDontScroll();
      }
    )
    // Duplicates exercise in the Program Builder
    .post(
      "/exercise/duplicate",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query } = req;
        const proxyAthlete = query.proxyAthlete;

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const cycle = await api(
          `cycle-duplicate-exercise?proxyAthlete=${proxyAthlete}`,
          {
            method: "POST",
            body,
          }
        );
        res.backDontScroll();
      }
    )
    // Duplicates exercise for another workout in the Program Builder
    .post(
      "/exercise/duplicate-send-to-other-workout",
      async ({ req, res, assert, flash, locals, api }) => {
        const { body, query } = req;
        const proxyAthlete = query.proxyAthlete;

        assert(
          body.destination && body.destination.length,
          400,
          i18n.t("coaching-portal:exerciseActions.selectPlaceToDuplicateTo")
        );

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const cycle = await api(
          `cycle-duplicate-exercise-to-other-workout?proxyAthlete=${proxyAthlete}`,
          {
            method: "POST",
            body,
          }
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Remove a set from an exercise in the quick-edit.
    .post(
      "/exercise/quick-edit/sets/remove/:index/:exercise/:organizationCoach/:proxyAthlete?",
      async ({ req, res, flash, api }) => {
        const { params, query = {}, body } = req;
        const { index, organizationCoach = undefined, proxyAthlete } = params;
        const { reqSource } = query;

        await quickEditRemoveSetRequests(
          body,
          api,
          proxyAthlete,
          reqSource,
          flash,
          index,
          organizationCoach
        );

        res.refreshWith(
          {
            edit: true,
            editExerciseId: body.editExercise._id,
            weekIndex: body.editExercise.cycle?.programWeek,
            sessionIndex: body.editExercise.cycle?.programSession - 1,
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Update progression to exercise in the Program Builder
    .post(
      "/exercise/update-progression-to-exercise",
      async ({ req, res, api, flash, session }) => {
        const { body } = req;

        // some how in some random cases a key/val pair is getting sent with undefined:undefined. This is just to clean up the body
        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const response = await api(`cycle-progression-exercise`, {
          method: "PUT",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyUpdated")
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Update progression to session in the Program Builder
    .post(
      "/exercise/update-progression-to-session",
      async ({ req, res, api, flash, session }) => {
        const { body } = req;

        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const response = await api(`cycle-progression-session`, {
          method: "PUT",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyUpdated")
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    // Update progression to week in the Program Builder
    .post(
      "/exercise/update-progression-to-week",
      async ({ req, res, api, flash, session }) => {
        const { body, query = {} } = req;

        Object.keys(body).forEach(
          (key) => key === "undefined" && delete body[key]
        );

        const calendarMode = get(query, "calendarMode", false);

        const response = await api(
          calendarMode ? "calendar-progression" : "cycle-progression-week",
          {
            method: "PUT",
            body,
          }
        );
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyUpdated")
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post("/exercise/superset/add", async ({ req, res, api, flash }) => {
      const {
        exercise,
        nextExercise,
        fullExercise,
        monthCalendar = false,
      } = req.body;
      await api(
        `exercise-set-next-superset?exercise=${exercise}&nextExercise=${nextExercise}`,
        {
          method: "PUT",
          body: {},
        }
      );

      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullyAdded")
      );

      if (monthCalendar) {
        return res.refreshWith({}, { url: "back" });
      }
      res.refreshWith(
        Object.assign(
          {},
          {
            edit: true,
            editExerciseId: fullExercise._id,
          },
          fullExercise.cycle &&
            fullExercise.cycle.programWeek && {
              weekIndex: fullExercise.cycle.programWeek,
            },
          fullExercise.cycle &&
            fullExercise.cycle.programSession && {
              sessionIndex: fullExercise.cycle.programSession - 1,
            }
        ),

        { url: "back" }
      );
    })
    .post("/exercise/superset/remove", async ({ req, res, api, flash }) => {
      const { exercise, fullExercise } = req.body;
      await api(`exercise-remove-next-superset?exercise=${exercise}`, {
        method: "PUT",
        body: {},
      });
      flash(
        "success",
        i18n.t("coaching-portal:exerciseActions.successfullyRemoved")
      );
      res.refreshWith(
        Object.assign(
          {},
          {
            edit: true,
            editExerciseId: fullExercise._id,
          },
          fullExercise.cycle &&
            fullExercise.cycle.programWeek && {
              weekIndex: fullExercise.cycle.programWeek,
            },
          fullExercise.cycle &&
            fullExercise.cycle.programSession && {
              sessionIndex: fullExercise.cycle.programSession - 1,
            }
        ),

        { url: "back" }
      );
    })
    .post(
      "/exercise/edit-video-record",
      async ({ req, res, flash, api, locals, assert }) => {
        const { date, shareLink, athleteId, _id, type, reportDate } = req.body;
        const { height, width } = shareLink;
        const sharedUrl = normalizeLoomURL(shareLink.sharedUrl);
        const embedUrl = normalizeLoomURL(shareLink.embedUrl);

        assert(
          sharedUrl,
          400,
          i18n.t("coaching-portal:exerciseActions.videoUrlNotEmpty")
        );

        assert(
          validLoomURL(sharedUrl),
          400,
          i18n.t("coaching-portal:exerciseActions.videoUrlNotValid")
        );

        await api(`video-record${_id ? `/${_id}` : ""}`, {
          method: _id ? "PUT" : "POST",
          body: {
            date,
            width,
            height,
            sharedUrl,
            userId: athleteId,
            coachId: locals.activeUser._id,
            embedUrl,
            avatar:
              !!locals.activeUser.avatar && !!locals.activeUser.avatar.file
                ? locals.activeUser.avatar.file
                : null,
            firebaseProjectId: locals.activeUserToRequest.firebaseProjectId
              ? locals.activeUserToRequest.firebaseProjectId
              : defaultFirebaseProjectId,
            type,
            reportDate,
          },
        });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySaved")
        );

        res.backDontScroll();
      }
    )
    .get(
      "/exercise/delete-video-record/:_id",
      async ({ req, res, flash, api }) => {
        const { _id } = req.params;

        await api(`video-record/${_id}`, {
          method: "DELETE",
        });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullyDeleted")
        );

        res.backDontScroll();
      }
    )
    .post(
      "/exercise/swap",
      async ({ api, assert, flash, load, req, res, locals }) => {
        const { body } = req;
        const {
          typeFrom,
          primaryFrom,
          typeTo,
          primaryTo,
          movementTo,
          secondaryFrom,
          proxyAthlete,
        } = body;

        assert(
          typeFrom,
          400,
          i18n.t("coaching-portal:exerciseActions.typeSwapFromRequired")
        );
        assert(
          primaryFrom,
          400,
          i18n.t("coaching-portal:exerciseActions.exerciseSwapFromRequired")
        );
        assert(
          typeTo,
          400,
          i18n.t("coaching-portal:exerciseActions.typeSwapToRequired")
        );
        assert(
          primaryTo,
          400,
          i18n.t("coaching-portal:exerciseActions.exerciseSwapToRequired")
        );
        assert(
          movementTo,
          400,
          i18n.t("coaching-portal:exerciseActions.nameSwapToRequired")
        );
        if (typeFrom === "secondary") {
          assert(
            secondaryFrom,
            400,
            i18n.t("coaching-portal:exerciseActions.exerciseSwapFromRequired")
          );
        }

        // add secondary movement to athlete's library
        if (proxyAthlete && typeTo !== "primary") {
          await load("athlete", { user: proxyAthlete });
          const { activeUserToRequest, athlete: profileUser } = locals;
          const coachLibrary =
            activeUserToRequest.exerciseLibrary[primaryTo] || [];
          const libraryMovement = coachLibrary.find(
            ({ movement }) => movement === movementTo
          );
          if (libraryMovement) {
            const movement = Object.assign({}, libraryMovement, {
              // use the athlete's RM
              repMaximum: profileUser.repMaximums[primaryTo],
              primary: primaryTo,
            });
            await copySecondaryMovement(
              movement,
              profileUser,
              api,
              proxyAthlete
            );
          }
        }

        await api("swap-exercise", {
          method: "POST",
          body,
        });
        flash(
          "success",
          i18n.t("coaching-portal:exerciseActions.successfullySwapped")
        );

        res.refreshWith(
          { modal: "", editMode: "", exercise: "" },
          { url: "back", hash: "#_" }
        );
      }
    )
    // Create or update an exercise
    .post(
      "/exercise/edit-set-video",
      async ({ req, res, flash, locals, api, load }) => {
        const { body, params, query = {} } = req;

        await load("editExercise", {
          exercise: params.exercise,
          proxyAthlete: query.proxyAthlete,
        });

        const { editExercise } = locals;
        const _id = get(editExercise, "_id");

        await api(`review-video-exercise`, { method: "PUT", body: body });

        res.refreshWith(
          {
            editMode: "",
            movement: "",
            scrollTo: _id,
            reviewedByTheCoach: true,
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/exercise/add-video-thread-comment",
      async ({ req, assert, res, flash, locals, api }) => {
        const { body } = req;
        const { message, senderId, setIndex, videoIndex, exerciseId } = body;
        let { threadId } = body;
        let creatingNewThread = false;

        assert(senderId, 400, "Sender ID is required");
        assert(message, 400, "Message is required");

        try {
          if (!threadId) {
            creatingNewThread = true;
            assert(exerciseId, 400, "Exercise ID is required");
            assert(setIndex || setIndex === 0, 400, "Set index is required");
            assert(
              videoIndex || videoIndex === 0,
              400,
              "Video index is required"
            );
            const response = await api(`thread`, {
              method: "POST",
            });

            const data = await response.json();

            if (!data._id) {
              throw new Error("Failed to create thread");
            }

            threadId = data._id;

            await api(`exercise-set-video/${exerciseId}`, {
              method: "PUT",
              body: {
                setIndex,
                videoIndex,
                threadId,
              },
            });
          }

          await api("comment", {
            method: "POST",
            body: {
              message,
              senderId,
              receiverId: body.receiverId,
              threadId,
              activityContext: body.activityContext,
            },
          });
          flash("success", "Successfully added comment");
          res.refreshWith(
            { ...(creatingNewThread && { threadId }) },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/exercise/delete-video-thread-comment/:_id",
      async ({ req, res, flash, api, assert }) => {
        const { _id } = req.params;
        const { fullDelete, setIndex, videoIndex, exerciseId, threadId } =
          req.body;
        assert(_id, 400, "Comment ID is required");

        // fullDelete will happen when all the comments are deleted from
        // the thread, so instead of making a soft delete, we get rid of them
        if (fullDelete) {
          // we need the exercise data to detach the thread from the exercise
          assert(exerciseId, 400, "Exercise ID is required");
          assert(setIndex || setIndex === 0, 400, "Set index is required");
          assert(
            videoIndex || videoIndex === 0,
            400,
            "Video index is required"
          );
          assert(threadId, 400, "Thread ID is required");

          // detach the thread from the exercise
          await api(`exercise-set-video/${exerciseId}`, {
            method: "PUT",
            body: {
              setIndex,
              videoIndex,
              threadId: null,
              deleteThread: true,
            },
          });
          // delete the thread
          await api(`thread/${threadId}`, { method: "DELETE" });
          // delete all thread's comments
          await api(`comment?threadId=${threadId}&fullDelete=true`, {
            method: "DELETE",
          });
          flash("success", "Successfully deleted thread");
          return res.refreshWith(
            { threadId: null },
            { url: "back", hash: "#_" }
          );
        } else {
          // otherwise, we just delete the single comment (soft delete - check api)
          await api(`comment/${_id}`, { method: "DELETE" });
          flash("success", "Successfully deleted comment");
          return res.refreshWith({}, { url: "back", hash: "#_" });
        }
      }
    )
    // Just simply edit the comment content
    .post(
      "/exercise/edit-video-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { commentId, message } = body;

        assert(commentId, 400, "Comment ID is required");
        assert(message, 400, "Message is required");

        await api(`comment/${commentId}`, {
          method: "PUT",
          body: { message, editedAt: new Date() },
        });

        flash("success", "Comment saved successfully");

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    );
};

export default Exercise;
